import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { DataSourceGet } from '../../models/datasource/datasource-get.model';
import { DataService } from '../../services/data.service';
import { DataSourcePickerComponent } from '../../components/data-source-picker/data-source-picker.component';
import { LayoutService, ViewMode } from '../../services/layout.service';
import { Subscription } from 'rxjs';

interface ReadinessDataSource {
  dataSourceId: string;
  dataSourceName: string;
  timestampColumn: string;
  selectedColumns: string[];
  columns: string[];
  timestampColumns: string[];
  isLoadingSchema: boolean;
}

@Component({
  selector: 'app-ai-readiness',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    DataSourcePickerComponent
  ],
  templateUrl: './ai-readiness.component.html',
  styleUrl: './ai-readiness.component.scss'
})
export class AiReadinessComponent implements OnInit, OnDestroy {
  dataSources: ReadinessDataSource[] = [];
  availableDataSources: DataSourceGet[] = [];
  isLoadingDataSources = false;
  projectType = 'classification';
  useCase = 'predictive-maintenance';
  isRunning = false;
  showResults = false;
  configurationExpanded = false;
  private viewModeSubscription?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dataService: DataService,
    public readonly layoutService: LayoutService
  ) {
    const stateSources = (this.router.getCurrentNavigation()?.extras.state?.['dataSources'] ??
      history.state?.dataSources) as Array<{ dataSourceId: string; dataSourceName: string }> | undefined;

    this.dataSources = (stateSources ?? [])
      .map(source => this.createDataSource(source.dataSourceId, source.dataSourceName));
  }

  ngOnInit(): void {
    this.configurationExpanded = this.layoutService.isAdvancedMode();
    this.viewModeSubscription = this.layoutService.viewMode$.subscribe(mode => {
      if (mode === 'advanced') {
        this.configurationExpanded = true;
      }
    });
    this.loadAvailableDataSources();
  }

  ngOnDestroy(): void {
    this.viewModeSubscription?.unsubscribe();
  }

  setViewMode(mode: ViewMode): void {
    this.layoutService.setViewMode(mode);
    if (mode === 'advanced') {
      this.configurationExpanded = true;
    }
  }

  toggleConfiguration(): void {
    this.configurationExpanded = !this.configurationExpanded;
  }

  get selectedDataSourceIds(): string[] {
    return this.dataSources.map(source => source.dataSourceId);
  }

  openDataSourcePicker(): void {
    if (!this.availableDataSources.length && !this.isLoadingDataSources) {
      this.loadAvailableDataSources();
    }
  }

  addDataSource(dataSource: DataSourceGet): void {
    const dataSourceId = dataSource.dataSourceId;
    if (!dataSourceId || this.dataSources.some(source => source.dataSourceId === dataSourceId)) {
      return;
    }

    const selectedSource = this.createDataSource(dataSourceId, dataSource.dataSourceName);
    this.dataSources = [...this.dataSources, selectedSource];
    this.loadDataSourceSchema(selectedSource);
  }

  removeDataSource(dataSource: ReadinessDataSource): void {
    this.dataSources = this.dataSources.filter(source => source.dataSourceId !== dataSource.dataSourceId);
  }

  onColumnsSelectionChange(dataSource: ReadinessDataSource, selectedColumns: string[]): void {
    if (selectedColumns.includes('__select_all_columns__')) {
      dataSource.selectedColumns = [...dataSource.columns];
    } else {
      dataSource.selectedColumns = selectedColumns;
    }
  }

  startAssessment(): void {
    if (!this.dataSources.length || this.isRunning) return;

    this.isRunning = true;
    this.showResults = false;
    setTimeout(() => {
      this.isRunning = false;
      this.showResults = true;
    }, 700);
  }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  private createDataSource(dataSourceId: string, dataSourceName: string): ReadinessDataSource {
    return {
      dataSourceId,
      dataSourceName,
      timestampColumn: '',
      selectedColumns: [],
      columns: [],
      timestampColumns: [],
      isLoadingSchema: false
    };
  }

  private loadAvailableDataSources(): void {
    this.isLoadingDataSources = true;
    this.dataService.getDataSourceList().subscribe({
      next: dataSources => {
        this.availableDataSources = (dataSources ?? []).filter(dataSource => dataSource.dataSourceTypeId !== 1);
        this.isLoadingDataSources = false;
        this.dataSources.forEach(dataSource => this.loadDataSourceSchema(dataSource));
      },
      error: error => {
        console.error('Failed to load AI Readiness data sources:', error);
        this.availableDataSources = [];
        this.isLoadingDataSources = false;
      }
    });
  }

  private loadDataSourceSchema(dataSource: ReadinessDataSource): void {
    dataSource.isLoadingSchema = true;
    this.dataService.getDataSource(dataSource.dataSourceId).subscribe({
      next: sourceDetails => {
        const schemaColumns = this.parseSchemaColumns(sourceDetails.schemaJson);
        dataSource.columns = schemaColumns.map(column => column.name);
        dataSource.timestampColumns = schemaColumns
          .filter(column => this.isDateTimeColumn(column.type))
          .map(column => column.name);
        dataSource.isLoadingSchema = false;
      },
      error: error => {
        console.error(`Failed to load schema for datasource ${dataSource.dataSourceId}:`, error);
        dataSource.columns = [];
        dataSource.timestampColumns = [];
        dataSource.isLoadingSchema = false;
      }
    });
  }

  private parseSchemaColumns(schemaJson: string | null | undefined): Array<{ name: string; type: string }> {
    if (!schemaJson) {
      return [];
    }

    try {
      const schema = JSON.parse(schemaJson);
      const rawColumns = Array.isArray(schema)
        ? schema
        : schema.columns ?? schema.Columns ?? schema.schema ?? [];
      const columns = Array.isArray(rawColumns) ? rawColumns : Object.values(rawColumns);
      return columns
        .map((column: any) => ({
          name: column.column ?? column.column_name ?? column.name ?? column.Name ?? column.ColumnName ?? column.columnName,
          type: column.atomic_dtype ?? column.soft_dtype ?? column.dtype ?? column.type ?? column.Type ?? column.DataType ?? column.dataType ?? ''
        }))
        .filter((column: { name?: string }) => !!column.name);
    } catch (error) {
      console.error('Failed to parse datasource schema:', error);
      return [];
    }
  }

  private isDateTimeColumn(type: string): boolean {
    return /date|time|timestamp|datetime/i.test(type);
  }
}